import React, { FC, useEffect, useMemo, useState } from 'react'
import { Company, CompanyAdmin, Crew, Rank, Vessel } from '../core/_models'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import { getCrewSignOnDate, isDateBeforeSignOn, toSignOnDateKey } from '../core/signOnDateUtils'
import SearchableSelect from '../../../shared/components/SearchableSelect'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const FIELD_LABEL_STYLE: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.55rem',
  color: '#3F4254',
  fontWeight: 600,
  fontSize: '0.95rem',
}

const CONTROL_CLASS = 'form-control text-dark bg-white'
const SELECT_CLASS = 'form-select text-dark bg-white'

const COMPLAINT_TYPE_OPTIONS = [
  { value: 'BULLYING', label: 'Bullying' },
  { value: 'PHYSICAL_HARASSMENT', label: 'Physical Harassment' },
  { value: 'MENTAL_HARASSMENT', label: 'Mental Harassment' },
  { value: 'SEXUAL_HARASSMENT', label: 'Sexual Harassment' },
] as const

export type ComplaintFormValues = {
  id?: number
  crewId?: number
  vesselId?: number
  rankId?: number
  recordDate: string
  reportDate: string
  seafarerName: string
  complaintType: string
  description: string
  assignedHodRole: '' | 'CHIEF_OFFICER' | 'CHIEF_ENGINEER'
  hodMasterResponse: string
  hodMasterResponseDate: string
  onboardPreventRecurrence: string
  hodSignature: string
  masterSignature: string
  onboardReviewRole: '' | 'HOD' | 'MASTER'
  onboardResolved: '' | 'YES' | 'NO'
  officeResolved: '' | 'YES' | 'NO'
  officeActionTaken: string
  officeActionDate: string
  officePreventRecurrence: string
  officeWelfareOfficerSignature: string
  officeResolutionDate: string
  authoritiesActionTaken: string
  authoritiesActionDate: string
  authoritiesPreventRecurrence: string
  authoritiesWelfareOfficerSignature: string
  authoritiesResolutionDate: string
  officeRemarks: string
}

type Props = {
  isOpen: boolean
  isSaving: boolean
  isSuperAdmin: boolean
  isCrew: boolean
  isCrewMaster: boolean
  mode: 'create' | 'edit'
  form: ComplaintFormValues
  ranks: Rank[]
  crewMembers: Crew[]
  companyGroups: CompanyAdmin[]
  subCompanies: Company[]
  vessels: Vessel[]
  pendingFiles: File[]
  onClose: () => void
  onSave: () => void
  onOpenAttachments: () => void
  onPendingFilesChange: (files: File[]) => void
  onFieldChange: (field: keyof ComplaintFormValues, value: string | number | undefined) => void
  crewSignOnDateById?: Map<number, string>
}

const CrewComplaintModal: FC<Props> = ({
  isOpen,
  isSaving,
  isSuperAdmin,
  isCrew,
  isCrewMaster,
  mode,
  form,
  ranks,
  crewMembers,
  companyGroups,
  subCompanies,
  vessels,
  pendingFiles,
  onClose,
  onSave,
  onOpenAttachments,
  onPendingFilesChange,
  onFieldChange,
  crewSignOnDateById = new Map<number, string>(),
}) => {
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  useEffect(() => {
    if (!isOpen) return
    setSelectedVesselId(form.vesselId || '')
  }, [isOpen, form.vesselId])

  const getSubCompanyGroupId = (company: Company): number | undefined => {
    return (company as any)?.cga?.id ?? (company as any)?.cgaid?.id ?? undefined
  }

  const getVesselCompanyAdminId = (v: Vessel): number | undefined => {
    return v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
  }

  const getVesselCompanyGroupId = (v: Vessel): number | undefined => {
    return (
      (v as any).companyGroupAdminId ??
      v.companyGroupAdmin?.id ??
      (v.companyAdmin as any)?.cgaid?.id ??
      (v.companyAdmin as any)?.cga?.id
    )
  }

  const filteredSubCompanies = useMemo(() => {
    if (!selectedCompanyGroupId) return subCompanies
    return subCompanies.filter((company) => getSubCompanyGroupId(company) === Number(selectedCompanyGroupId))
  }, [subCompanies, selectedCompanyGroupId])

  const shouldShowSubCompanyFilter = Boolean(selectedCompanyGroupId) && filteredSubCompanies.length > 0

  const filteredVessels = useMemo(() => {
    if (selectedCompanyAdminId) {
      return vessels.filter((vessel) => getVesselCompanyAdminId(vessel) === Number(selectedCompanyAdminId))
    }
    if (selectedCompanyGroupId) {
      return vessels.filter((vessel) => getVesselCompanyGroupId(vessel) === Number(selectedCompanyGroupId))
    }
    return vessels
  }, [vessels, selectedCompanyAdminId, selectedCompanyGroupId])

  const filteredCrewMembers = useMemo(() => {
    return crewMembers.filter((crew) => {
      const matchesCompanyGroup = !selectedCompanyGroupId || crew.companyGroupAdminId === Number(selectedCompanyGroupId)
      const matchesCompanyAdmin = !selectedCompanyAdminId || crew.companyAdminId === Number(selectedCompanyAdminId)
      const matchesVessel = !selectedVesselId || (crew.vessel?.id || crew.vesselId) === Number(selectedVesselId)
      return matchesCompanyGroup && matchesCompanyAdmin && matchesVessel
    })
  }, [crewMembers, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId])

  const rankById = useMemo(() => new Map(ranks.map((r) => [r.id, r.rank])), [ranks])

  const handleCrewChange = (crewIdRaw: string) => {
    const crewId = Number(crewIdRaw)
    const selected = filteredCrewMembers.find((c) => c.id === crewId)
    if (!selected) {
      onFieldChange('crewId', undefined)
      return
    }

    const signOnDate = getCrewSignOnDate(selected.id, crewSignOnDateById) || toSignOnDateKey(selected.latestSignOnDate)
    const nextRecordDate = signOnDate && (!form.recordDate || form.recordDate < signOnDate)
      ? signOnDate
      : form.recordDate
    const nextReportDate = signOnDate && (!form.reportDate || form.reportDate < signOnDate)
      ? signOnDate
      : form.reportDate

    onFieldChange('crewId', selected.id)
    onFieldChange('vesselId', selected.vessel?.id || selected.vesselId || undefined)
    onFieldChange('rankId', selected.rankId || undefined)
    onFieldChange('seafarerName', selected.name)
    onFieldChange('recordDate', nextRecordDate)
    onFieldChange('reportDate', nextReportDate)
  }

const isMasterRankLabel = (rankName?: string | null) => {
  const normalized = String(rankName || '').toLowerCase()
  return normalized.includes('master')
}

const isHodRankLabel = (rankName?: string | null) => {
  const normalized = String(rankName || '').toLowerCase()
  return (
    normalized.includes('chief officer') ||
    normalized.includes('chief engineer') ||
    normalized.includes('c/o') ||
    normalized.includes('c/e')
  )
}

  if (!isOpen) return null

  const selectedCrewSignOnDate = getCrewSignOnDate(form.crewId, crewSignOnDateById)

  const selectedCrew = form.crewId ? crewMembers.find((c) => c.id === form.crewId) : undefined
  const selectedRankName =
    (selectedCrew as any)?.rankName ||
    (selectedCrew?.rankId ? rankById.get(selectedCrew.rankId) : undefined) ||
    (form.rankId ? rankById.get(form.rankId) : undefined)
  const isMasterSeafarer = isMasterRankLabel(selectedRankName)
  const isHodSeafarer = isHodRankLabel(selectedRankName)
  const hideHodAssignment = isMasterSeafarer || isHodSeafarer

  const rankLabel = form.rankId ? rankById.get(form.rankId) || '-' : '-'
  const vesselLabel =
    (form.vesselId ? vessels.find((v) => v.id === form.vesselId)?.fleet_name : '') ||
    (form.vesselId ? crewMembers.find((c) => (c.vessel?.id || c.vesselId) === form.vesselId)?.vessel?.fleet_name : '') ||
    '-'

  return (
    <div style={WRAP_STYLE}>
      <div className='bg-white rounded shadow-lg' style={{ width: '78vw', maxWidth: 1050, maxHeight: '88vh', overflow: 'auto' }}>
        <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
          <h5 className='modal-title'>{mode === 'create' ? 'Create Crew Complaint' : 'Edit Crew Complaint'}</h5>
          <button type='button' className='btn-close' onClick={onClose} disabled={isSaving} />
        </div>

        <div className='p-5'>
          {!isCrew && (
            <div className='row g-3 mb-4'>
              {isSuperAdmin && (
                <>
                  <div className='col-md-4'>
                    <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Company</label>
                    <SearchableSelect
                      className={SELECT_CLASS}
                      value={selectedCompanyGroupId}
                      onChange={(e) => {
                        const next = e.target.value ? Number(e.target.value) : ''
                        setSelectedCompanyGroupId(next)
                        setSelectedCompanyAdminId('')
                        setSelectedVesselId('')
                        onFieldChange('crewId', undefined)
                        onFieldChange('vesselId', undefined)
                        onFieldChange('rankId', undefined)
                      }}
                    >
                      <option value=''>All Companies</option>
                      {companyGroups.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </SearchableSelect>
                  </div>

                  {shouldShowSubCompanyFilter && (
                    <div className='col-md-4'>
                      <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Sub-Company</label>
                      <SearchableSelect
                        className={SELECT_CLASS}
                        value={selectedCompanyAdminId}
                        onChange={(e) => {
                          const next = e.target.value ? Number(e.target.value) : ''
                          setSelectedCompanyAdminId(next)
                          setSelectedVesselId('')
                          onFieldChange('crewId', undefined)
                          onFieldChange('vesselId', undefined)
                          onFieldChange('rankId', undefined)
                        }}
                      >
                        <option value=''>All Sub-Companies</option>
                        {filteredSubCompanies.map((ca) => (
                          <option key={ca.id} value={ca.id}>{ca.name}</option>
                        ))}
                      </SearchableSelect>
                    </div>
                  )}
                </>
              )}

              <div className='col-md-4'>
                <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Vessel</label>
                <SearchableSelect
                  className={SELECT_CLASS}
                  value={selectedVesselId}
                  onChange={(e) => {
                    const next = e.target.value ? Number(e.target.value) : ''
                    setSelectedVesselId(next)
                    onFieldChange('crewId', undefined)
                    onFieldChange('vesselId', next ? Number(next) : undefined)
                    onFieldChange('rankId', undefined)
                  }}
                >
                  <option value=''>All Vessels</option>
                  {filteredVessels.map((v) => (
                    <option key={v.id} value={v.id}>{v.fleet_name}</option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='col-md-8'>
                <label className='form-label required fw-semibold' style={FIELD_LABEL_STYLE}>Crew</label>
                <SearchableSelect
                  className={SELECT_CLASS}
                  value={form.crewId || ''}
                  disabled={mode === 'edit'}
                  onChange={(e) => handleCrewChange(e.target.value)}
                >
                  <option value=''>Select crew</option>
                  {filteredCrewMembers.map((crew) => (
                    <option key={crew.id} value={crew.id}>{crew.name}</option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='col-md-4'>
                <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Rank</label>
                <input className={CONTROL_CLASS} value={rankLabel} disabled />
              </div>
            </div>
          )}

          <div className='row g-3 mb-4'>
            <div className='col-md-3'>
              <label className='form-label required fw-semibold' style={FIELD_LABEL_STYLE}>Record Date</label>
              <input
                type='date'
                className={CONTROL_CLASS}
                value={form.recordDate}
                onChange={(e) => {
                  const nextDate = e.target.value
                  onFieldChange('recordDate', nextDate)
                }}
              />
            </div>
            <div className='col-md-3'>
              <label className='form-label required fw-semibold' style={FIELD_LABEL_STYLE}>Date of Report</label>
              <input
                type='date'
                className={CONTROL_CLASS}
                value={form.reportDate}
                onChange={(e) => {
                  const nextDate = e.target.value
                  onFieldChange('reportDate', nextDate)
                }}
              />
              {selectedCrewSignOnDate && (
                <small className='text-muted'>
                  Date cannot be before sign-on date ({selectedCrewSignOnDate}).
                </small>
              )}
            </div>
            <div className='col-md-3'>
              <label className='form-label fw-semibold' style={FIELD_LABEL_STYLE}>Vessel</label>
              <div className={CONTROL_CLASS} style={{ display: 'flex', alignItems: 'center' }}>
                <VesselLandingLink
                  vesselId={form.vesselId ?? null}
                  vesselName={vesselLabel}
                  className='fw-semibold text-dark text-hover-primary'
                />
              </div>
            </div>
            <div className='col-md-3'>
              <label className='form-label required fw-semibold' style={FIELD_LABEL_STYLE}>Complaint Type</label>
              <SearchableSelect
                className={SELECT_CLASS}
                value={form.complaintType || ''}
                onChange={(e) => onFieldChange('complaintType', e.target.value)}
              >
                <option value=''>Select complaint type</option>
                {COMPLAINT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </SearchableSelect>
            </div>
          </div>

          <div className='mb-4'>
            <label className='form-label required fw-semibold' style={FIELD_LABEL_STYLE}>Description of Complaint</label>
            <textarea
              className={CONTROL_CLASS}
              rows={5}
              value={form.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
            />
          </div>

          {!hideHodAssignment && (
            <div className='row g-3 mb-4'>
              <div className='col-md-4'>
                <label className='form-label required fw-semibold' style={FIELD_LABEL_STYLE}>Assign To (HOD)</label>
                <SearchableSelect
                  className={SELECT_CLASS}
                  value={form.assignedHodRole}
                  onChange={(e) => onFieldChange('assignedHodRole', e.target.value as '' | 'CHIEF_OFFICER' | 'CHIEF_ENGINEER')}
                >
                  <option value=''>Select HOD</option>
                  <option value='CHIEF_OFFICER'>Chief Officer</option>
                  <option value='CHIEF_ENGINEER'>Chief Engineer</option>
                </SearchableSelect>
              </div>
            </div>
          )}

          <div className='mb-3 border rounded p-4'>
            <h6 className='fw-bold mb-3'>Attachments</h6>
            <input
              type='file'
              className={CONTROL_CLASS}
              multiple
              onChange={(e) => onPendingFilesChange(Array.from(e.target.files || []))}
            />
            {pendingFiles.length > 0 && (
              <div className='text-muted mt-2'>{pendingFiles.length} file(s) selected for upload.</div>
            )}
          </div>

          {mode === 'edit' && form.id && (
            <button type='button' className='btn btn-light-primary btn-sm' onClick={onOpenAttachments}>
              Manage Existing Attachments
            </button>
          )}
        </div>

        <div className='d-flex justify-content-end gap-3 border-top px-5 py-4'>
          <button className='btn btn-light' onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className='btn btn-primary' onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewComplaintModal
